import { forwardRef, type ReactNode } from 'react';
import { Card as MantineCard, type CardProps as MantineCardProps } from '@mantine/core';
import { alpha, colors } from '../../theme/tokens';

export type CardVariant = 'default' | 'elevated' | 'danger' | 'warning';

export interface ForgeCardProps extends Omit<MantineCardProps, 'children'> {
  variant?: CardVariant;
  hoverable?: boolean;
  selected?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
}

/**
 * Card — base surface primitive (clean SaaS; see DESIGN-SAAS.md).
 *
 * Wraps Mantine `Card` with Forge variants. The border + glow tint shifts by
 * variant; `selected` is for list/grid items that act as radio buttons;
 * `hoverable` enables the subtle lift used in catalog cards.
 *
 * Footer slot renders inside a `Card.Section` divider — pairs well with KPI
 * tiles that need a "view details" action below the metric.
 */
const VARIANT_BORDER: Record<CardVariant, string> = {
  default: 'var(--c-bd)',
  elevated: 'var(--c-bd-bright)',
  danger: colors.hudRed,
  warning: colors.hudAmber,
};

const VARIANT_GLOW: Record<CardVariant, string | undefined> = {
  default: undefined,
  elevated: `0 6px 24px -12px rgba(34, 197, 94, 0.18)`,
  danger: `0 0 0 1px ${alpha('hudRed', 0.2)}`,
  warning: `0 0 0 1px ${alpha('hudAmber', 0.2)}`,
};

export const Card = forwardRef<HTMLDivElement, ForgeCardProps>(function Card(
  { variant = 'default', hoverable = false, selected = false, footer, children, style, ...rest },
  ref,
) {
  const borderColor = selected ? colors.neon : VARIANT_BORDER[variant];
  const boxShadow = VARIANT_GLOW[variant];

  return (
    <MantineCard
      ref={ref}
      // Matched to the `.hud-panel` surface in global.css (12px radius,
      // --shadow-card, 16px padding) so the two card systems can no longer
      // render different radii/shadows side by side on the same screen.
      radius={12}
      padding="md"
      withBorder
      data-forge-card=""
      data-hoverable={hoverable ? 'true' : undefined}
      style={{
        backgroundColor: 'rgb(var(--c-card))',
        borderColor,
        boxShadow: boxShadow ?? 'var(--shadow-card)',
        transition: 'transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
        ...style,
      }}
      {...rest}
    >
      {children}
      {footer ? (
        <MantineCard.Section
          inheritPadding
          py="sm"
          mt="md"
          style={{ borderTop: `1px solid ${VARIANT_BORDER.default}` }}
        >
          {footer}
        </MantineCard.Section>
      ) : null}
    </MantineCard>
  );
});
